import { useState, useEffect, useRef } from 'react';
import { Download, Loader2, FileText } from 'lucide-react';
import { renderAsync } from 'docx-preview';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { documentsService } from '../services';
import type { CandidateDocument } from '../types';

const PREVIEWABLE_NATIVE = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/bmp',
];

const DOCX_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
];

interface DocumentPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  document: CandidateDocument | null;
  candidateId: number;
}

/**
 * Dialog for previewing candidate documents.
 * Supports PDF (iframe), images (img), and DOCX/DOC (docx-preview).
 * Falls back to a download button for unsupported formats.
 */
export function DocumentPreviewDialog({
  open,
  onOpenChange,
  document,
  candidateId,
}: DocumentPreviewDialogProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [docxReady, setDocxReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const docxContainerRef = useRef<HTMLDivElement>(null);

  const isNativePreview = document
    ? PREVIEWABLE_NATIVE.includes(document.mimeType)
    : false;

  const isDocx = document
    ? DOCX_MIME_TYPES.includes(document.mimeType) || /\.docx?$/i.test(document.fileName)
    : false;

  const isPreviewable = isNativePreview || isDocx;
  const isImage = document?.mimeType.startsWith('image/') ?? false;

  useEffect(() => {
    if (!open || !document || !isPreviewable) {
      return;
    }

    let cancelled = false;

    const loadPreview = async () => {
      setIsLoading(true);
      setError(null);
      setDocxReady(false);
      setPreviewUrl(null);

      try {
        const blob = await documentsService.downloadBlob(candidateId, document.id);

        if (cancelled) return;

        if (isDocx) {
          const arrayBuffer = await blob.arrayBuffer();
          if (!cancelled && docxContainerRef.current) {
            docxContainerRef.current.innerHTML = '';
            await renderAsync(arrayBuffer, docxContainerRef.current, undefined, {
              className: 'docx-preview',
              inWrapper: true,
            });
            if (!cancelled) {
              setDocxReady(true);
            }
          }
        } else {
          const url = window.URL.createObjectURL(blob);
          if (!cancelled) {
            setPreviewUrl(url);
          } else {
            window.URL.revokeObjectURL(url);
          }
        }
      } catch (err) {
        console.error('[DocumentPreview] Erreur chargement:', err);
        if (!cancelled) {
          setError('Impossible de charger le document');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadPreview();

    return () => {
      cancelled = true;
    };
  }, [open, document?.id, candidateId, isPreviewable, isDocx]);

  const handleDownload = async () => {
    if (!document) return;
    try {
      const blob = await documentsService.downloadBlob(candidateId, document.id);
      documentsService.triggerDownload(blob, document.fileName);
    } catch {
      setError('Erreur lors du téléchargement');
    }
  };

  const handleClose = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    if (docxContainerRef.current) {
      docxContainerRef.current.innerHTML = '';
    }
    setPreviewUrl(null);
    setDocxReady(false);
    setError(null);
    onOpenChange(false);
  };

  if (!document) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>{document.fileName}</DialogTitle>
          <DialogDescription>
            {document.fileSizeFormatted} — {document.mimeType}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-[60vh]">
          {isLoading ? (
            <div className="flex items-center justify-center h-[60vh]">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
              <p className="text-sm text-destructive">{error}</p>
              <Button variant="outline" onClick={handleDownload}>
                <Download className="mr-2 h-4 w-4" />
                Télécharger
              </Button>
            </div>
          ) : previewUrl ? (
            isImage ? (
              <img
                src={previewUrl}
                alt={document.fileName}
                className="max-w-full max-h-[60vh] mx-auto object-contain"
              />
            ) : (
              <iframe
                src={previewUrl}
                title={document.fileName}
                className="w-full h-[60vh] border rounded"
              />
            )
          ) : !isPreviewable ? (
            <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
              <FileText className="h-16 w-16 text-muted-foreground" />
              <p className="text-muted-foreground">
                Aperçu non disponible pour ce type de fichier
              </p>
              <Button onClick={handleDownload}>
                <Download className="mr-2 h-4 w-4" />
                Télécharger le fichier
              </Button>
            </div>
          ) : null}
          {/* Conteneur DOCX : toujours monté pour que le ref soit disponible, masqué si pas prêt */}
          <div
            ref={docxContainerRef}
            className={`overflow-y-auto max-h-[60vh] bg-white rounded border ${
              docxReady && !isLoading && !error ? '' : 'hidden'
            }`}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
